import { useId, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Collapse } from "@/components/design/Collapse";
import { ProgressBar } from "@/components/design/ProgressBar";
import { powerBars, type PowerBar } from "@/lib/games/roleplay/bars";
import type { Bars, StoryLanguage } from "@/lib/games/roleplay/contract";
import { DoneTick } from "./DoneTick";
import { powerName } from "./labels";

// One horizontal bar per chosen Power shows how many questionnaire statements the story has covered.
// The game's threshold is a neutral mark on each bar; reaching it changes no colour of the bar. A reached bar wears the
// green tick of a reached goal (DoneTick) beside its figure, and says to a screen reader that enough statements are answered
// („spark#1075, Entscheidung 127.61“).
//
// A bar with statements opens like a dropdown (97, amending 80), in the tutorial and in a scored game alike: a
// click on the bar lists every statement the bar counts for the power, the open ones first, the answered ones
// last with the green tick of a reached goal (DoneTick) and, for a screen reader, the word "answered". Texts only,
// never a value. It is Save Studio's toggle (StepChainTrigger): a real button with its chevron and the list in a
// Collapse. The button shows the power's name, says what it opens to a screen reader, and covers the whole bar; the
// percent stays outside it, so a figure that moves never renames it. A bar without statements and the guide's example
// bars are no buttons.
//
// The guide uses the same horizontal bars with example progress and no statement list.

function Bar({ bar, name, mark, language }: { bar: PowerBar; name: string; mark: number; language: StoryLanguage }) {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const listId = useId();
  const captionId = useId();
  const listed = bar.statements.length > 0;
  // The share as the page's language writes a percentage: "45 %" in German, "45%" in English.
  const percent = (
    <span className="flex shrink-0 items-baseline gap-1.5">
      {bar.reached ? (
        <>
          <DoneTick />
          <span className="sr-only">
            <FormattedMessage id="grow.roleplay.bars.reached" defaultMessage="enough statements answered" />
          </span>
        </>
      ) : null}
      <span className={`smycles-field-meta tabular-nums ${bar.reached ? "text-ink" : "text-ink-muted"}`}>
        {intl.formatMessage({ id: "grow.roleplay.bars.percent", defaultMessage: "{percent}%" }, { percent: bar.percent })}
      </span>
    </span>
  );
  // The mark hangs from the track's bottom edge: ProgressBar prints its count above the track
  // (@adr SPARK-0053-the-signal-colour-stays-unchanged@2), so the top of this box is no longer the track's.
  const track = (
    <div className="relative mt-1.5">
      <ProgressBar label={name} done={bar.covered} total={bar.total} />
      <span aria-hidden="true" className="absolute -bottom-1 h-3.5 w-px bg-ink/40" style={{ left: `${mark}%` }} />
    </div>
  );
  if (!listed) {
    return (
      <li>
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-smycles-body">{name}</span>
          {percent}
        </div>
        {track}
      </li>
    );
  }
  return (
    <li>
      {/* The button's overlay (after:) spans name, figure and bar, so a click anywhere on the bar opens the list. */}
      <div className="relative -mx-2 -my-1 px-2 py-1">
        <div className="flex items-baseline justify-between gap-3">
          <button
            type="button"
            onClick={() => setOpen((shown) => !shown)}
            aria-expanded={open}
            aria-controls={listId}
            aria-label={`${name}: ${intl.formatMessage({ id: "grow.roleplay.bars.statementsToggle", defaultMessage: "questions from the questionnaire" })}`}
            className="flex cursor-pointer select-none items-center gap-1.5 text-left text-smycles-body after:absolute after:inset-0 after:z-10 after:rounded focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-ink-ui"
          >
            {name}
            <span
              aria-hidden="true"
              className={`flex items-center text-ink-muted transition-transform duration-[var(--duration-base)] ease-in-out motion-reduce:transition-none ${open ? "rotate-180" : ""}`}
            >
              <svg viewBox="0 0 24 24" className="size-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="m6 9 6 6 6-6" />
              </svg>
            </span>
          </button>
          {percent}
        </div>
        {track}
      </div>
      <div id={listId}>
        <Collapse open={open}>
          <div className="mt-2 border-l border-ink/10 pl-3">
            <p id={captionId} className="smycles-field-label">
              <FormattedMessage id="grow.roleplay.bars.statements" defaultMessage="Questions from the questionnaire" />
            </p>
            <ul aria-labelledby={captionId} className="mt-1">
              {bar.statements.map((statement, index) => (
                <li key={index} className="flex items-baseline gap-2 py-1 text-smycles-meta leading-relaxed">
                  {statement.answered ? (
                    <DoneTick />
                  ) : (
                    <span aria-hidden="true" className="text-ink-muted">
                      ○
                    </span>
                  )}
                  <span className="text-ink">
                    {statement.text[language]}
                    {statement.answered ? (
                      <span className="sr-only">
                        {" "}
                        <FormattedMessage id="grow.roleplay.bars.statementAnswered" defaultMessage="answered" />
                      </span>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </Collapse>
      </div>
    </li>
  );
}

export function PowerBars({ bars, language }: { bars: Bars; language: StoryLanguage }) {
  const intl = useIntl();
  return (
    <div>
      <ul className="space-y-3">
        {powerBars(bars).map((bar) => (
          <Bar key={bar.power} bar={bar} name={powerName(intl, bar.power)} mark={bars.thresholdPercent} language={language} />
        ))}
      </ul>
      <p className="mt-3 smycles-field-meta text-ink-muted">
        <FormattedMessage
          id="grow.roleplay.bars.mark"
          defaultMessage="The line marks {percent}%."
          values={{ percent: bars.thresholdPercent }}
        />
      </p>
    </div>
  );
}
